import { useState, useEffect } from 'react';
import { X, Car, Bike, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { vehicleService } from '../../services/vehicleService';

interface Vehicle {
  id: string;
  type: '2wheeler' | '3wheeler' | '4wheeler';
  make: string;
  model: string;
  registrationNumber?: string;
  connectorTypes?: string[];
  maxChargingSpeedKw?: number;
  year?: number;
}

interface AddVehicleSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (vehicle: Omit<Vehicle, 'id'>) => void;
  editingVehicle?: Vehicle | null;
}

interface VehicleTemplate {
  make: string;
  model: string;
  years: number[];
  connectorTypes: string[];
  maxChargingSpeedKw: number;
  vehicleType?: '2wheeler' | '3wheeler' | '4wheeler';
}

export function AddVehicleSheet({ open, onOpenChange, onSave, editingVehicle }: AddVehicleSheetProps) {
  const [type, setType] = useState<'2wheeler' | '3wheeler' | '4wheeler'>('4wheeler');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [errors, setErrors] = useState<{ make?: string; model?: string }>({});
  const [touched, setTouched] = useState<{ make?: boolean; model?: boolean }>({});
  const [vehicleTemplates, setVehicleTemplates] = useState<VehicleTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<VehicleTemplate | null>(null);

  // Fetch vehicle templates when sheet opens
  useEffect(() => {
    if (open) {
      fetchVehicleTemplates();
    }
  }, [open]);

  // Reset form when editing vehicle changes or sheet opens/closes
  useEffect(() => {
    if (editingVehicle) {
      setType(editingVehicle.type);
      setMake(editingVehicle.make);
      setModel(editingVehicle.model);
      setRegistrationNumber(editingVehicle.registrationNumber || '');
    } else {
      setType('4wheeler');
      setMake('');
      setModel('');
      setRegistrationNumber('');
    }
    setErrors({});
    setTouched({});
    setSelectedTemplate(null);
  }, [editingVehicle, open]);

  const fetchVehicleTemplates = async () => {
    try {
      setIsLoadingTemplates(true);
      const data = await vehicleService.getReferenceData();
      setVehicleTemplates(data.vehicleTemplates);
    } catch (error) {
      console.error('Failed to fetch vehicle templates:', error);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  if (!open) return null;

  // Filter templates by vehicle type
  const filteredTemplates = vehicleTemplates.filter(
    (template) => !template.vehicleType || template.vehicleType === type
  );

  // Get unique makes for the selected type
  const availableMakes = Array.from(
    new Set(filteredTemplates.map((template) => template.make))
  ).sort();

  // Get models for the selected make
  const availableModels = filteredTemplates
    .filter((template) => template.make === make)
    .map((template) => template.model)
    .sort();

  // Reset model when make changes
  const handleMakeChange = (newMake: string) => {
    setMake(newMake);
    setModel('');
    setSelectedTemplate(null);
    if (touched.make) {
      setErrors({ ...errors, make: newMake ? '' : 'Please select a make' });
    }
  };

  // Update selected template when model changes
  const handleModelChange = (newModel: string) => {
    setModel(newModel);

    // Find the matching template
    const template = filteredTemplates.find(
      (t) => t.make === make && t.model === newModel
    );
    setSelectedTemplate(template || null);

    if (touched.model) {
      setErrors({ ...errors, model: newModel ? '' : 'Please select a model' });
    }
  };

  // Reset make and model when type changes
  const handleTypeChange = (newType: '2wheeler' | '3wheeler' | '4wheeler') => {
    setType(newType);
    setMake('');
    setModel('');
    setSelectedTemplate(null);
    setErrors({});
    setTouched({});
  };

  const validateForm = () => {
    const newErrors: { make?: string; model?: string } = {};

    if (!make) newErrors.make = 'Please select a make';
    if (!model) newErrors.model = 'Please select a model';

    setErrors(newErrors);
    setTouched({ make: true, model: true });

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave({
        type,
        make,
        model,
        registrationNumber: registrationNumber.trim(),
        connectorTypes: selectedTemplate?.connectorTypes || [],
        maxChargingSpeedKw: selectedTemplate?.maxChargingSpeedKw,
        year: selectedTemplate?.years?.[selectedTemplate.years.length - 1] || new Date().getFullYear(),
      });
    }
  };

  const vehicleTypes = [
    { value: '2wheeler', label: 'Two Wheeler', icon: Bike, color: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800' },
    { value: '3wheeler', label: 'Three Wheeler', icon: Car, color: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-800' },
    { value: '4wheeler', label: 'Four Wheeler', icon: Car, color: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-[2001] flex items-end sm:items-center justify-center">
      <div className="bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}
            </h2>
            <button
              onClick={() => onOpenChange(false)}
              className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Vehicle Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Vehicle Type *
            </label>
            <div className="grid grid-cols-3 gap-3">
              {vehicleTypes.map((vehicleType) => {
                const Icon = vehicleType.icon;
                const isSelected = type === vehicleType.value;
                return (
                  <button
                    key={vehicleType.value}
                    onClick={() => handleTypeChange(vehicleType.value as any)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-[#8bc43e] bg-[#8bc43e]/10'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <Icon className={`w-6 h-6 mx-auto mb-2 ${isSelected ? 'text-[#8bc43e]' : 'text-gray-400 dark:text-gray-500'}`} />
                    <p className={`text-xs font-medium ${isSelected ? 'text-[#8bc43e]' : 'text-gray-600 dark:text-gray-400'}`}>
                      {vehicleType.label.replace(' Wheeler', 'W')}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Loading State */}
          {isLoadingTemplates && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Loader2 className="w-5 h-5 animate-spin text-[#8bc43e]" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Loading vehicle data...</span>
            </div>
          )}

          {/* Make Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Make *
            </label>
            <select
              value={make}
              onChange={(e) => handleMakeChange(e.target.value)}
              onBlur={() => setTouched({ ...touched, make: true })}
              disabled={isLoadingTemplates}
              className={`w-full h-14 px-4 rounded-xl border-2 ${
                touched.make && errors.make
                  ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
                  : 'border-gray-200 dark:border-gray-700 focus:border-[#8bc43e]'
              } outline-none transition-colors appearance-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white cursor-pointer disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed`}
            >
              <option value="">Select make</option>
              {availableMakes.map((makeName) => (
                <option key={makeName} value={makeName}>
                  {makeName}
                </option>
              ))}
            </select>
            {touched.make && errors.make && (
              <div className="flex items-center gap-2 mt-2 text-red-600 dark:text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.make}</span>
              </div>
            )}
          </div>

          {/* Model Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Model *
            </label>
            <select
              value={model}
              onChange={(e) => handleModelChange(e.target.value)}
              onBlur={() => setTouched({ ...touched, model: true })}
              disabled={!make || isLoadingTemplates}
              className={`w-full h-14 px-4 rounded-xl border-2 ${
                touched.model && errors.model
                  ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
                  : 'border-gray-200 dark:border-gray-700 focus:border-[#8bc43e]'
              } outline-none transition-colors appearance-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white cursor-pointer disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed`}
            >
              <option value="">Select model</option>
              {availableModels.map((modelName) => (
                <option key={modelName} value={modelName}>
                  {modelName}
                </option>
              ))}
            </select>
            {touched.model && errors.model && (
              <div className="flex items-center gap-2 mt-2 text-red-600 dark:text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.model}</span>
              </div>
            )}
            {!make && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Select a make first to see available models</p>
            )}
          </div>

          {/* Registration Number (Optional) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Registration Number (Optional)
            </label>
            <input
              type="text"
              value={registrationNumber}
              onChange={(e) => setRegistrationNumber(e.target.value.toUpperCase())}
              className="w-full h-14 px-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 focus:border-[#8bc43e] outline-none transition-colors font-mono bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
              placeholder="KL-07-AB-1234"
              maxLength={15}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              This helps identify your vehicle. You can skip this if you prefer.
            </p>
          </div>

          {/* Connector Types Info */}
          {selectedTemplate && selectedTemplate.connectorTypes.length > 0 && (
            <div className="bg-gradient-to-br from-[#8bc43e]/10 to-[#6fa02e]/10 border border-[#8bc43e]/30 rounded-xl p-4">
              <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Compatible Connectors
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedTemplate.connectorTypes.map((connectorId) => (
                  <span
                    key={connectorId}
                    className="inline-flex items-center px-3 py-1 rounded-lg bg-white dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
                  >
                    {connectorId.toUpperCase()}
                  </span>
                ))}
              </div>
              {selectedTemplate.maxChargingSpeedKw && (
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                  Max charging speed: {selectedTemplate.maxChargingSpeedKw} kW
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] rounded-b-3xl">
          <div className="flex gap-3">
            <Button
              onClick={() => onOpenChange(false)}
              className="flex-1 h-14 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-white rounded-xl font-semibold"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1 h-14 bg-[#8bc43e] hover:bg-[#7ab32e] text-white rounded-xl font-semibold"
            >
              {editingVehicle ? 'Update' : 'Add'} Vehicle
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}